// src/components/TrackingDetails.js
import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosHandler';

const TrackingDetails = ({ order, isAdmin }) => {
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Function to format date from ISO string
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status color based on shipping status
  const getStatusColor = (status) => {
    switch (status) {
      case 'created':
        return 'bg-blue-100 text-blue-800';
      case 'in_transit':
        return 'bg-indigo-100 text-indigo-800';
      case 'out_for_delivery':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'exception':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get human-readable status text
  const getStatusText = (status) => {
    switch (status) {
      case 'created':
        return 'Shipping Label Created';
      case 'in_transit':
        return 'In Transit';
      case 'out_for_delivery':
        return 'Out for Delivery';
      case 'delivered':
        return 'Delivered';
      case 'exception':
        return 'Delivery Exception';
      default:
        return 'Pending';
    }
  };

  // Only fetch tracking data if order is shipped
  useEffect(() => {
    const fetchTrackingData = async () => {
      if (!order || !order._id || (order.status !== 'shipped' && order.status !== 'completed')) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/orders/${order._id}/track`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setTrackingData(response.data.tracking);
      } catch (err) {
        console.error('Error fetching tracking data:', err);
        setError(err.response?.data?.error || 'Error retrieving tracking information');
      } finally {
        setLoading(false);
      }
    };

    fetchTrackingData();
  }, [order, refreshKey]);

  // If order is not shipped yet
  if (!order || (order.status !== 'shipped' && order.status !== 'completed')) {
    return (
      <div className="mt-4 bg-gray-50 p-4 rounded-lg">
        <div className="text-gray-600 text-sm">
          <p>Tracking information will be available once the order is shipped.</p>
        </div>
      </div>
    );
  }

  // If no shipping data yet
  if (!order.shipping || !order.shipping.trackingNumber) {
    return (
      <div className="mt-4 bg-gray-50 p-4 rounded-lg">
        <div className="text-gray-600 text-sm">
          <p>Shipment is being processed. Tracking details will be available soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 bg-white border rounded-lg overflow-hidden">
      <div className="bg-gray-50 p-4 border-b">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Shipment Tracking</h3>
          <button
            onClick={() => setRefreshKey(old => old + 1)}
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
            disabled={loading}
          >
            {loading ? (
              <span>Refreshing...</span>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {error ? (
        <div className="p-4 text-red-600">
          <p>{error}</p>
        </div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-600">Carrier:</p>
              <p className="font-semibold">{order.shipping.carrier}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Tracking Number:</p>
              <div className="flex space-x-2 items-center">
                <p className="font-semibold">{order.shipping.trackingNumber}</p>
                <a
                  href={`https://www.canadapost-postescanada.ca/track-reperage/en#/search?searchFor=${order.shipping.trackingNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Track on carrier website
                </a>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status:</p>
              <p className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.shipping.status)}`}>
                {getStatusText(order.shipping.status)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Estimated Delivery:</p>
              <p className="font-semibold">
                {order.shipping.estimatedDelivery
                  ? formatDate(order.shipping.estimatedDelivery)
                  : 'Not available'}
              </p>
            </div>
          </div>

          {isAdmin && (
            <div className="mb-4 flex space-x-2">
              <a
                href={`/api/orders/${order._id}/shipping-label`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Shipping Label
              </a>
            </div>
          )}

          {/* Tracking Timeline */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium mb-3">Tracking History</h4>
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            ) : trackingData?.events && trackingData.events.length > 0 ? (
              <div className="space-y-4">
                {trackingData.events.map((event, index) => (
                  <div key={index} className="relative pl-8">
                    {/* Timeline dot and line */}
                    <div className="absolute left-0 top-0 mt-1.5">
                      <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                      {index < trackingData.events.length - 1 && (
                        <div className="absolute top-3 left-1.5 w-0.5 h-full -ml-px bg-gray-300"></div>
                      )}
                    </div>

                    {/* Event content */}
                    <div>
                      <p className="font-medium text-sm">{event.description}</p>
                      <div className="text-xs text-gray-500 flex space-x-3">
                        <span>{formatDate(event.date)} {event.time}</span>
                        <span>{event.location}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : order.shipping.events && order.shipping.events.length > 0 ? (
              <div className="space-y-4">
                {order.shipping.events.map((event, index) => (
                  <div key={index} className="relative pl-8">
                    {/* Timeline dot and line */}
                    <div className="absolute left-0 top-0 mt-1.5">
                      <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                      {index < order.shipping.events.length - 1 && (
                        <div className="absolute top-3 left-1.5 w-0.5 h-full -ml-px bg-gray-300"></div>
                      )}
                    </div>

                    {/* Event content */}
                    <div>
                      <p className="font-medium text-sm">{event.description}</p>
                      <div className="text-xs text-gray-500 flex space-x-3">
                        <span>{formatDate(event.date)} {event.time}</span>
                        <span>{event.location}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-3 text-gray-600 text-sm">
                <p>No detailed tracking events available yet.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackingDetails;